import { useEffect, useState } from 'react'

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyPreview } from '@/components/ui/money-preview'
import { Textarea } from '@/components/ui/textarea'
import { useCreateDebtAdjustment } from '@/hooks/use-customer-debt-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { todayYMD } from '@/lib/date-presets'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'

interface Props {
    customerId: string
    currentDebt: number
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function CustomerDebtAdjustmentDialog({
    customerId,
    currentDebt,
    open,
    onOpenChange,
}: Props) {
    const workflow = useWorkflowTracking('customer_debt_adjustment', {
        active: open,
        attemptKey: customerId,
        operation: 'create',
    })
    // target giữ dạng chuỗi để phân biệt "chưa nhập" với 0.
    const [target, setTarget] = useState('')
    const [occurredAt, setOccurredAt] = useState(todayYMD())
    const [note, setNote] = useState('')

    const createAdjustment = useCreateDebtAdjustment(customerId)

    useEffect(() => {
        if (open) {
            setTarget(String(currentDebt))
            setOccurredAt(todayYMD())
            setNote('')
        }
    }, [open, currentDebt])

    const hasTarget = target.trim() !== '' && !isNaN(Number(target))
    const targetValue = Number(target)
    const delta = hasTarget ? targetValue - currentDebt : 0

    async function handleSubmit() {
        await workflow.run(
            () =>
                createAdjustment.mutateAsync({
                    target_balance: targetValue,
                    // Ngày = hôm nay → gửi undefined để BE đóng dấu giờ thật (now()),
                    // tránh bút toán bị lưu 00:00 lệch dòng thời gian. Chỉ gửi ngày khi
                    // người dùng backdate.
                    occurred_at:
                        occurredAt && occurredAt !== todayYMD()
                            ? occurredAt
                            : undefined,
                    note: note || undefined,
                }),
            (result) => result.data?.adjustment.id,
        )
        onOpenChange(false)
    }

    const canSubmit = hasTarget && delta !== 0 && !createAdjustment.isPending

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{m.customer_debt_adjust_title()}</DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    <div>
                        <Label htmlFor="adj-target" className="mb-1 block">
                            {m.customer_debt_adjust_target()}
                        </Label>
                        <Input
                            id="adj-target"
                            type="number"
                            step={1}
                            value={target}
                            onChange={(e) => {
                                workflow.interact('payment')
                                setTarget(e.target.value)
                            }}
                            aria-describedby="adj-target-preview"
                        />
                        <MoneyPreview id="adj-target-preview" value={target} />
                    </div>
                    <div>
                        <Label htmlFor="adj-date" className="mb-1 block">
                            {m.customer_debt_adjust_date()}
                        </Label>
                        <Input
                            id="adj-date"
                            type="date"
                            value={occurredAt}
                            onChange={(e) => {
                                workflow.interact('details')
                                setOccurredAt(e.target.value)
                            }}
                        />
                    </div>
                    <div>
                        <Label htmlFor="adj-note" className="mb-1 block">
                            {m.customer_debt_adjust_note()}
                        </Label>
                        <Textarea
                            id="adj-note"
                            value={note}
                            onChange={(e) => {
                                workflow.interact('details')
                                setNote(e.target.value)
                            }}
                        />
                    </div>

                    {/* Xem trước tác động: dư hiện tại → dư mới + delta live */}
                    <div className="rounded-md border p-3 text-sm space-y-1">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">
                                {m.customer_debt_adjust_current()}
                            </span>
                            <span>{formatVnd(currentDebt)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">
                                {m.customer_debt_adjust_new()}
                            </span>
                            <span className="font-medium">
                                {hasTarget ? formatVnd(targetValue) : '—'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">
                                {m.customer_debt_adjust_delta()}
                            </span>
                            <span
                                className={
                                    delta > 0
                                        ? 'text-red-600 font-medium'
                                        : delta < 0
                                          ? 'text-green-600 font-medium'
                                          : ''
                                }
                            >
                                {delta > 0 ? '+' : ''}
                                {formatVnd(delta)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={createAdjustment.isPending}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button onClick={handleSubmit} disabled={!canSubmit}>
                        {createAdjustment.isPending
                            ? m.common_processing()
                            : m.customer_debt_adjust_submit()}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
